// Per-vault cache preferences — max size cap + expiration choice.
//
// Storage: encrypted, inside `appConfig.preferences.cache_prefs_by_vault_id`
// at `/system/config.enc`. The legacy plaintext-localStorage path is
// gone — every read goes through the master session, every write goes
// through `saveAppConfig` and refreshes the in-memory master session.
//
// The vault-wide `enabled` flag (the "should this vault use the cache
// at all" boolean) lives inside the vault and is managed by the
// worker, not by this module.

import type {
  AppConfig,
  CacheExpiration as AppConfigCacheExpiration,
  CachePrefs as AppConfigCachePrefs,
  VaultWorkerApi,
} from "../worker/api";
import { useMasterSession } from "../stores/masterSession";

export type CacheExpiration =
  | { kind: "never-expire" }
  | { kind: "expire-after"; durationMs: number }
  | { kind: "session-only" };

export interface DeviceCachePrefs {
  formatVersion: 1;
  maxBytes: number;
  expiration: CacheExpiration;
}

export const DEFAULT_DEVICE_PREFS: DeviceCachePrefs = {
  formatVersion: 1,
  maxBytes: 500 * 1024 * 1024,
  expiration: { kind: "never-expire" },
};

/**
 * Read the per-vault cache prefs for `vaultId` from the unlocked
 * master session. Returns the defaults if no master session is
 * unlocked, or if the vault has no entry in
 * `appConfig.preferences.cache_prefs_by_vault_id` yet.
 *
 * Synchronous because the master session is an in-memory zustand
 * store and the app-config is loaded once at master-unlock time.
 */
export function readDevicePrefs(vaultId: string): DeviceCachePrefs {
  const cfg = useMasterSession.getState().master?.appConfig;
  if (!cfg) return DEFAULT_DEVICE_PREFS;
  const entry = cfg.preferences.cache_prefs_by_vault_id[vaultId];
  if (!entry) return DEFAULT_DEVICE_PREFS;
  return fromAppConfigPrefs(entry);
}

/**
 * Update the per-vault cache prefs for `vaultId` in the master
 * session's app-config and persist via the worker's `saveAppConfig`.
 *
 * No-op (without throwing) if no master session is currently
 * unlocked — there's nowhere to persist to. The cache prefs UI
 * should never be reachable from outside an unlocked master, so
 * this case is defensive only.
 */
export async function writeDevicePrefs(
  vault: VaultWorkerApi,
  vaultId: string,
  prefs: DeviceCachePrefs,
): Promise<void> {
  const master = useMasterSession.getState().master;
  if (!master?.appConfig) return;
  const next: AppConfig = {
    ...master.appConfig,
    preferences: {
      ...master.appConfig.preferences,
      cache_prefs_by_vault_id: {
        ...master.appConfig.preferences.cache_prefs_by_vault_id,
        [vaultId]: toAppConfigPrefs(prefs),
      },
    },
  };
  await vault.saveAppConfig(master.masterHandle, next);
  useMasterSession.getState().setAppConfig(next);
}

function fromAppConfigPrefs(p: AppConfigCachePrefs): DeviceCachePrefs {
  return {
    formatVersion: 1,
    maxBytes: p.max_bytes,
    expiration: fromAppConfigExpiration(p.expiration),
  };
}

function toAppConfigPrefs(p: DeviceCachePrefs): AppConfigCachePrefs {
  return {
    max_bytes: p.maxBytes,
    expiration: toAppConfigExpiration(p.expiration),
  };
}

function fromAppConfigExpiration(
  e: AppConfigCacheExpiration,
): CacheExpiration {
  if (e.kind === "expire-after") {
    return { kind: "expire-after", durationMs: e.duration_ms };
  }
  return { kind: e.kind };
}

function toAppConfigExpiration(
  e: CacheExpiration,
): AppConfigCacheExpiration {
  if (e.kind === "expire-after") {
    return { kind: "expire-after", duration_ms: e.durationMs };
  }
  return { kind: e.kind };
}
